import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import type {
  ProviderWebhookDelivery,
  ProviderWebhookDeliveryStatus,
  ProviderWebhookEndpoint,
  ProviderWebhookEvent,
  UpsertProviderWebhookInput,
} from '@/types/provider-webhooks';
import {
  PROVIDER_WEBHOOK_DELIVERY_STATUSES,
  PROVIDER_WEBHOOK_EVENTS,
} from '@/types/provider-webhooks';

interface ApiPayload {
  data?: unknown;
  [key: string]: unknown;
}

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const readNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const normalizeEvent = (value: unknown): ProviderWebhookEvent | null => {
  if (typeof value !== 'string') {
    return null;
  }

  return PROVIDER_WEBHOOK_EVENTS.includes(value as ProviderWebhookEvent)
    ? (value as ProviderWebhookEvent)
    : null;
};

const normalizeStatus = (value: unknown): ProviderWebhookDeliveryStatus => {
  if (typeof value !== 'string') {
    return 'pending';
  }

  return PROVIDER_WEBHOOK_DELIVERY_STATUSES.includes(value as ProviderWebhookDeliveryStatus)
    ? (value as ProviderWebhookDeliveryStatus)
    : 'pending';
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function normalizeEndpoint(resource: Record<string, unknown>): ProviderWebhookEndpoint {
  const subscribedEventsRaw = resource.subscribed_events ?? resource.subscribedEvents;
  const subscribedEvents = Array.isArray(subscribedEventsRaw)
    ? subscribedEventsRaw.map(normalizeEvent).filter((value): value is ProviderWebhookEvent => value !== null)
    : [];
  const customHeaderKeysRaw = resource.custom_header_keys ?? resource.customHeaderKeys;
  const customHeaderKeys = Array.isArray(customHeaderKeysRaw)
    ? customHeaderKeysRaw.filter((value): value is string => typeof value === 'string')
    : [];

  return {
    id: readString(resource.id) ?? '',
    name: readString(resource.name) ?? '',
    url: readString(resource.url) ?? '',
    hasSecret: readBoolean(resource.has_secret ?? resource.hasSecret) ?? false,
    hasCustomHeaders: readBoolean(resource.has_custom_headers ?? resource.hasCustomHeaders) ?? false,
    customHeaderKeys,
    subscribedEvents,
    timeout: readNumber(resource.timeout) ?? 10,
    enabled: readBoolean(resource.enabled) ?? true,
    createdAt: readString(resource.created_at ?? resource.createdAt) ?? '',
    updatedAt: readString(resource.updated_at ?? resource.updatedAt) ?? '',
  };
}

function normalizeEndpointCollection(payload: ApiPayload | undefined): ProviderWebhookEndpoint[] {
  const data = payload?.data;
  if (Array.isArray(data)) {
    return data
      .map(asRecord)
      .filter((record): record is Record<string, unknown> => record !== null)
      .map(normalizeEndpoint);
  }

  const record = asRecord(data);
  if (record) {
    return [normalizeEndpoint(record)];
  }

  return [];
}

function normalizeDelivery(resource: Record<string, unknown>): ProviderWebhookDelivery {
  return {
    id: readString(resource.id) ?? '',
    endpointId: readString(resource.endpoint_id ?? resource.endpointId) ?? '',
    eventName: readString(resource.event_name ?? resource.eventName) ?? '',
    status: normalizeStatus(resource.status),
    attempt: readNumber(resource.attempt) ?? 1,
    responseStatus: readNumber(resource.response_status ?? resource.responseStatus) ?? null,
    responseBody: readString(resource.response_body ?? resource.responseBody) ?? null,
    errorMessage: readString(resource.error_message ?? resource.errorMessage) ?? null,
    payload: asRecord(resource.payload),
    dispatchedAt: readString(resource.dispatched_at ?? resource.dispatchedAt) ?? null,
    deliveredAt: readString(resource.delivered_at ?? resource.deliveredAt) ?? null,
    nextRetryAt: readString(resource.next_retry_at ?? resource.nextRetryAt) ?? null,
    createdAt: readString(resource.created_at ?? resource.createdAt) ?? '',
    updatedAt: readString(resource.updated_at ?? resource.updatedAt) ?? '',
  };
}

function normalizeDeliveryCollection(payload: ApiPayload | undefined): ProviderWebhookDelivery[] {
  const data = payload?.data;
  if (Array.isArray(data)) {
    return data
      .map(asRecord)
      .filter((record): record is Record<string, unknown> => record !== null)
      .map(normalizeDelivery);
  }

  const record = asRecord(data);
  if (record) {
    return [normalizeDelivery(record)];
  }

  return [];
}

function formatWebhookPayload(input: UpsertProviderWebhookInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: input.name,
    url: input.url,
    subscribed_events: input.subscribedEvents,
    timeout: input.timeout,
    enabled: input.enabled,
  };

  if (input.secret !== undefined) {
    payload.secret = input.secret;
  }
  if (input.headers !== undefined) {
    payload.headers = input.headers;
  }

  return payload;
}

export class ProviderWebhooksRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestWebhooks(
  path: string,
  options: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: Record<string, unknown> } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderWebhooksRequestError(error.message, error.status, error.payload);
    }

    throw new ProviderWebhooksRequestError('Webhook request failed', undefined, error);
  }
}

class ProviderWebhooksService {
  async listWebhooks(): Promise<ProviderWebhookEndpoint[]> {
    const response = await requestWebhooks('/api/v1/provider/integrations/webhooks');
    return normalizeEndpointCollection(response);
  }

  async createWebhook(input: UpsertProviderWebhookInput): Promise<ProviderWebhookEndpoint> {
    const response = await requestWebhooks('/api/v1/provider/integrations/webhooks', {
      method: 'POST',
      body: formatWebhookPayload(input),
    });
    const [webhook] = normalizeEndpointCollection(response);
    return webhook;
  }

  async updateWebhook(webhookId: string, input: UpsertProviderWebhookInput): Promise<ProviderWebhookEndpoint> {
    const response = await requestWebhooks(`/api/v1/provider/integrations/webhooks/${webhookId}`, {
      method: 'PUT',
      body: formatWebhookPayload(input),
    });
    const [webhook] = normalizeEndpointCollection(response);
    return webhook;
  }

  async deleteWebhook(webhookId: string): Promise<void> {
    await requestWebhooks(`/api/v1/provider/integrations/webhooks/${webhookId}`, {
      method: 'DELETE',
    });
  }

  async listDeliveries(webhookId: string, limit = 25): Promise<ProviderWebhookDelivery[]> {
    const safeLimit = Math.max(1, Math.min(250, Math.floor(limit)));
    const response = await requestWebhooks(
      `/api/v1/provider/integrations/webhooks/${webhookId}/deliveries?limit=${safeLimit}`,
    );
    return normalizeDeliveryCollection(response);
  }
}

export const providerWebhooksService = new ProviderWebhooksService();
